import type { Operaciones } from '../../api/operaciones';
import type { AforoEvento, CambioDeFecha, ComoSeEntra, ConsultaEventos, EstadoEvento, Evento, OperacionesEventos, TipoEntrada } from '../../api/ops/eventos';
import { ErrorApi } from '../../api/problema';
import { cifraDinero } from '../../logica/dinero';
import type { TarjetaAyuda } from '../../marco/cabecera';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Reglas y textos de «Eventos» (10.1 a 10.5), sin estilos: filtros de la lista, fechas en la zona del evento, barra de
 * aforo, línea de ventas, pestañas de la ficha según los permisos, motivos de publicar y textos de los errores. El panel
 * no calcula dinero (los importes llegan de la API); solo cuenta plazas con las cifras de `capacity_detail`.
 */

// ---------------------------------------------------------------- Lista (10.1)

export type Filtro = 'proximos' | 'draft' | 'published' | 'ended' | 'cancelled';

export const FILTROS: readonly { valor: Filtro; etiqueta: string; vacia: string }[] = [
    { valor: 'proximos', etiqueta: 'Próximos', vacia: 'próximos' },
    { valor: 'draft', etiqueta: 'Borradores', vacia: 'en borrador' },
    { valor: 'published', etiqueta: 'Publicados', vacia: 'publicados' },
    { valor: 'ended', etiqueta: 'Finalizados', vacia: 'finalizados' },
    { valor: 'cancelled', etiqueta: 'Cancelados', vacia: 'cancelados' },
];

/**
 * Qué se pide a `listEvents` con cada filtro. «Próximos» (borradores y publicados que aún no han terminado) son dos
 * estados a la vez (G827) con lo que termina después de ahora (`from`).
 */
export function consultaDeFiltro(filtro: Filtro, sede: string | null, ahora: Date = new Date()): ConsultaEventos {
    const base: ConsultaEventos = sede === null ? {} : { location_id: sede };
    // «Próximos»: borradores y publicados en una sola llamada (G827).
    return filtro === 'proximos' ? { ...base, status: ['draft', 'published'], from: ahora.toISOString() } : { ...base, status: [filtro] };
}

export function enFiltro(filtro: Filtro, e: Pick<Evento, 'status'>): boolean {
    return filtro === 'proximos' ? e.status === 'draft' || e.status === 'published' : e.status === filtro;
}

export function filtroDe(valor: string | null): Filtro {
    return FILTROS.find((f) => f.valor === valor)?.valor ?? 'proximos';
}

export const ESTADO_EVENTO: Readonly<Record<EstadoEvento, readonly [string, EstadoChip]>> = {
    draft: ['Borrador', 'draft'],
    published: ['Publicado', 'confirmed'],
    ended: ['Finalizado', 'neutral'],
    cancelled: ['Cancelado', 'problem'],
};

export const MODO: Readonly<Record<ComoSeEntra, readonly [string, string]>> = {
    tickets: ['Con entradas', 'Se compra una entrada para entrar.'],
    tickets_and_lists: ['Entradas y listas', 'Entradas a la venta y listas de invitados de tus RRPP.'],
    lists_only: ['Solo listas', 'Se entra con la invitación de una lista.'],
};
export const MODOS: readonly ComoSeEntra[] = ['tickets', 'tickets_and_lists', 'lists_only'];

export function lineaModo(e: Pick<Evento, 'sale_mode' | 'nominative'>): string {
    return `${e.sale_mode === undefined ? '' : MODO[e.sale_mode][0]}${e.nominative === true ? ' · nominativas' : ''}`;
}

/** Plazas comprometidas: vendidas, pendientes de pago, invitados y cupo de taquilla sin vender (C9, G4). */
export function comprometidas(a: AforoEvento): number {
    return a.tickets_confirmed + a.tickets_held + a.guests_held + a.box_office_reserved;
}

export interface Tramo {
    etiqueta: string;
    plazas: number;
    color: string;
}

/** Tramos de la barra de aforo apilada (los colores de Informes 10.8), libres al final. */
export function tramosAforo(a: AforoEvento): Tramo[] {
    return [
        { etiqueta: 'Vendidas', plazas: a.tickets_confirmed, color: 'var(--rv-ink)' },
        { etiqueta: 'Pendientes de pago', plazas: a.tickets_held, color: 'var(--rv-warning)' },
        { etiqueta: 'Invitados', plazas: a.guests_held, color: 'var(--rv-fg-3)' },
        { etiqueta: 'Cupo de taquilla', plazas: a.box_office_reserved, color: 'var(--rv-rule-strong)' },
        { etiqueta: 'Libres', plazas: a.available, color: 'var(--rv-white)' },
    ];
}

export function lineaAforo(e: Pick<Evento, 'status' | 'capacity_detail'>): { cifra: string; libres: string; completo: boolean } {
    const a = e.capacity_detail;
    if (e.status === 'cancelled') {
        return { cifra: `Aforo ${String(a.capacity)}`, libres: '', completo: false };
    }
    const completo = a.available <= 0;
    return { cifra: `${String(comprometidas(a))} de ${String(a.capacity)}`, libres: completo ? 'Completo' : `${String(a.available)} libres`, completo };
}

/** Línea de ventas de la lista: «212 vendidas» con «6 pendientes de pago» debajo; borrador «Sin publicar». */
export function lineaVentas(e: Pick<Evento, 'status' | 'sale_mode' | 'capacity_detail'>): { linea: string; sub: string } {
    const a = e.capacity_detail;
    if (e.status === 'draft') {
        return { linea: 'Sin publicar', sub: e.sale_mode === 'lists_only' ? 'Solo listas' : '' };
    }
    if (e.sale_mode === 'lists_only') {
        return { linea: `${String(a.guests_held)} invitados`, sub: '' };
    }
    return { linea: `${String(a.tickets_confirmed)} vendidas`, sub: e.status === 'published' && a.tickets_held > 0 ? `${String(a.tickets_held)} pendientes de pago` : '' };
}

// ---------------------------------------------------------------- Fechas en la zona del evento

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export interface PartesFecha {
    anio: number;
    mes: number;
    dia: number;
    hora: number;
    minuto: number;
    semana: number;
}

/** Fecha y hora de un instante en una zona IANA (la del evento, `timezone`). */
export function partesEn(iso: string, zona: string): PartesFecha {
    const f = new Date(iso);
    const partes = new Intl.DateTimeFormat('en-US', { timeZone: zona, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23', weekday: 'short' }).formatToParts(f);
    const v = (t: string) => partes.find((p) => p.type === t)?.value ?? '0';
    const semana = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(v('weekday'));
    return { anio: Number(v('year')), mes: Number(v('month')), dia: Number(v('day')), hora: Number(v('hour')) % 24, minuto: Number(v('minute')), semana };
}

const dos = (n: number) => String(n).padStart(2, '0');

/** «Sáb 17 oct». */
export function diaCorto(iso: string, zona: string): string {
    const p = partesEn(iso, zona);
    return `${DIAS[p.semana] ?? ''} ${String(p.dia)} ${MESES[p.mes - 1] ?? ''}`;
}

/** «21:30». */
export function horaEn(iso: string, zona: string): string {
    const p = partesEn(iso, zona);
    return `${dos(p.hora)}:${dos(p.minuto)}`;
}

/** «Sáb 17 oct · 21:30». */
export function diaYHoraEn(iso: string | null | undefined, zona: string): string {
    return iso == null ? '' : `${diaCorto(iso, zona)} · ${horaEn(iso, zona)}`;
}

/** «Sáb 17 oct · 21:30 – 00:30». */
export function rangoEn(inicio: string | undefined, fin: string | undefined, zona: string): string {
    return inicio === undefined ? '' : `${diaYHoraEn(inicio, zona)}${fin === undefined ? '' : ` – ${horaEn(fin, zona)}`}`;
}

/** Valor de un campo `datetime-local` (`AAAA-MM-DDTHH:MM`) en la zona del evento; vacío sin fecha. */
export function aCampo(iso: string | null | undefined, zona: string): string {
    if (iso == null || iso === '') {
        return '';
    }
    const p = partesEn(iso, zona);
    return `${String(p.anio)}-${dos(p.mes)}-${dos(p.dia)}T${dos(p.hora)}:${dos(p.minuto)}`;
}

/** Del campo `AAAA-MM-DDTHH:MM` (hora de la zona del evento) al instante UTC con `Z`; null si no es válido. */
export function deCampo(valor: string, zona: string): string | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(valor);
    if (m === null) {
        return null;
    }
    const [anio, mes, dia, hora, minuto] = m.slice(1).map(Number) as [number, number, number, number, number];
    const deseado = Date.UTC(anio, mes - 1, dia, hora, minuto);
    let t = deseado;
    // Dos pasadas: el desfase de la zona puede cambiar justo en el cambio de hora.
    for (let i = 0; i < 2; i++) {
        const p = partesEn(new Date(t).toISOString(), zona);
        const visto = Date.UTC(p.anio, p.mes - 1, p.dia, p.hora, p.minuto);
        t += deseado - visto;
    }
    return new Date(t).toISOString().replace('.000Z', 'Z');
}

/** Zona del navegador, para un evento nuevo sin zona aún (la pone el servidor con la del negocio). */
export function zonaLocal(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Plazo para cancelar gratis que vale ahora (G659, G665, G679): el del último cambio real; si ese cambio no avisó o su plazo
 * ya pasó, no hay ninguno (un cambio real posterior, aunque sea sin aviso, cierra el anterior; las correcciones no cuentan).
 */
export function plazoVigente(cambios: readonly CambioDeFecha[] | undefined, ahora: Date = new Date()): CambioDeFecha | null {
    const reales = (cambios ?? []).filter((c) => c.kind === 'real_change').sort((a, b) => a.created_at.localeCompare(b.created_at));
    const ultimo = reales[reales.length - 1];
    if (ultimo === undefined || !ultimo.notify_attendees || ultimo.free_cancellation_until == null) {
        return null;
    }
    return new Date(ultimo.free_cancellation_until).getTime() > ahora.getTime() ? ultimo : null;
}

/** «Fecha cambiada · cancelar gratis hasta el 22 oct» (vacío sin plazo vigente). */
export function chipFechaCambiada(e: Pick<Evento, 'date_changes' | 'timezone'>, ahora: Date = new Date()): string {
    const c = plazoVigente(e.date_changes, ahora);
    if (c?.free_cancellation_until == null) {
        return '';
    }
    const p = partesEn(c.free_cancellation_until, e.timezone);
    return `Fecha cambiada · cancelar gratis hasta el ${String(p.dia)} ${MESES[p.mes - 1] ?? ''}`;
}

// ---------------------------------------------------------------- Ficha (10.2 a 10.5)

export type Pestana = 'datos' | 'entradas' | 'puerta' | 'publicar' | 'fecha' | 'cancelar' | 'informe';

export const PESTANAS: Readonly<Record<Pestana, readonly [string, NombreIcono]>> = {
    datos: ['Datos', 'file-text'],
    entradas: ['Entradas', 'ticket'],
    puerta: ['Puerta', 'door-open'],
    publicar: ['Publicar', 'megaphone'],
    fecha: ['Fecha', 'calendar-clock'],
    cancelar: ['Cancelar', 'calendar-x'],
    informe: ['Informe', 'receipt'],
};

export interface PermisosEventos {
    gestiona: boolean;
    publica: boolean;
    cambiaFecha: boolean;
    cancela: boolean;
    preparaPuerta: boolean;
    veInvitados: boolean;
}

export function permisosEventos(puede: (c: string) => boolean): PermisosEventos {
    return {
        gestiona: puede('entradas.eventos.gestionar'),
        publica: puede('entradas.eventos.publicar'),
        cambiaFecha: puede('entradas.fechas.corregir') || puede('entradas.fechas.cambiar'),
        cancela: puede('entradas.eventos.cancelar'),
        preparaPuerta: puede('entradas.taquilla.preparar'),
        veInvitados: puede('listas.listas.ver'),
    };
}

/**
 * Pestañas de la ficha (como el prototipo): Datos · Entradas · Puerta para todos; cancelado, más Informe; quien publica
 * ve Publicar en borrador y publicado; Fecha solo publicado; Cancelar en borrador y publicado. Finalizado, solo las tres.
 */
export function pestanasDe(e: Pick<Evento, 'status'>, p: PermisosEventos): Pestana[] {
    const base: Pestana[] = ['datos', 'entradas', 'puerta'];
    if (e.status === 'cancelled') {
        return [...base, 'informe'];
    }
    if (e.status === 'ended') {
        return base;
    }
    return [
        ...base,
        ...(p.publica ? (['publicar'] as const) : []),
        ...(e.status === 'published' && p.cambiaFecha ? (['fecha'] as const) : []),
        ...(p.cancela ? (['cancelar'] as const) : []),
    ];
}

/** ¿Se edita la ficha? Con el permiso, sin solo lectura y en borrador o publicado. */
export function editable(e: Pick<Evento, 'status'>, gestiona: boolean, soloLectura: boolean): boolean {
    return gestiona && !soloLectura && (e.status === 'draft' || e.status === 'published');
}

export const EDADES: readonly number[] = [16, 18, 21, 25];

export function textoEdad(edad: number | null | undefined): string {
    return edad == null ? 'Sin aviso de edad' : `Evento para mayores de ${String(edad)} años`;
}

/** Colores de la baldosa en la taquilla (los 8 del sistema; G786: `color` en hexadecimal). */
export const COLORES: readonly { nombre: string; hex: string }[] = [
    { nombre: 'Negro', hex: '#111111' },
    { nombre: 'Rojo', hex: '#DD1420' },
    { nombre: 'Azul', hex: '#2563EB' },
    { nombre: 'Morado', hex: '#7C4DDB' },
    { nombre: 'Rosa', hex: '#C2347F' },
    { nombre: 'Naranja', hex: '#C2560A' },
    { nombre: 'Verde', hex: '#2E7D46' },
    { nombre: 'Turquesa', hex: '#0E7F8A' },
];

export function nombreColor(hex: string | null | undefined): string {
    return hex == null ? 'Neutro' : (COLORES.find((c) => c.hex.toLowerCase() === hex.toLowerCase())?.nombre ?? hex);
}

/** `If-Match` de un tipo de entrada: su `etag` de la lista (G835). */
export function versionDeTipo(t: Pick<TipoEntrada, 'id' | 'version' | 'etag'>): string {
    // Si no llegara (respuesta antigua), el mismo valor que da el servidor desde su versión.
    return t.etag ?? `"${t.id}.${String(t.version)}"`;
}

export const ESTADO_TIPO: Readonly<Record<TipoEntrada['status'], readonly [string, EstadoChip]>> = {
    draft: ['Borrador', 'draft'],
    active: ['Activo', 'confirmed'],
    paused: ['Pausado', 'neutral'],
    archived: ['Archivado', 'neutral'],
};

/** «A la venta · online y taquilla», «En pausa: no se vende · online», «Venta desde el 20 oct · online». */
export function lineaTipo(t: TipoEntrada, zona: string, ahora: Date = new Date()): string {
    const donde = [t.sold_online === true ? 'online' : null, t.sold_at_box_office === true ? 'taquilla' : null].filter((x) => x !== null).join(' y ');
    const fecha = (iso: string) => {
        const p = partesEn(iso, zona);
        return `${String(p.dia)} ${MESES[p.mes - 1] ?? ''}`;
    };
    let cuando: string;
    if (t.status === 'paused') {
        cuando = 'En pausa: no se vende';
    } else if (t.status === 'archived') {
        cuando = 'Archivado: no se vende';
    } else if (t.sales_end_at != null && new Date(t.sales_end_at) <= ahora) {
        cuando = `Venta terminada el ${fecha(t.sales_end_at)}`;
    } else if (t.sales_start_at != null && new Date(t.sales_start_at) > ahora) {
        cuando = `Venta desde el ${fecha(t.sales_start_at)}`;
    } else {
        cuando = 'A la venta';
    }
    return [cuando, donde, t.description ?? ''].filter((x) => x !== '').join(' · ');
}

/** «88 vendidas» o «100 de 100» con cupo propio. */
export function vendidasTipo(t: TipoEntrada): string {
    return t.quota == null ? `${String(t.confirmed_count)} vendidas` : `${String(t.confirmed_count)} de ${String(t.quota)}`;
}

// ---------------------------------------------------------------- Publicar (10.2)

export interface Requisito {
    clave: 'tipos' | 'fechas' | 'aforo' | 'cuenta';
    titulo: string;
    ok: boolean;
    texto: string;
    /** Pestaña a la que lleva «Ir a…» si falla. */
    ir?: Pestana;
}

/** Texto de cada motivo del `422` de publishEvent (un error por requisito, `errors[].field` y `code`). */
export function motivoPublicar(error: { field: string; code: string; message?: string }): { clave: Requisito['clave']; texto: string } {
    if (error.field === 'ticket_types') {
        return { clave: 'tipos', texto: 'Añade al menos un tipo de entrada activo.' };
    }
    if (error.field === 'sale_mode' && error.code === 'lists_only_with_ticket_types') {
        return { clave: 'tipos', texto: 'Es solo de listas: archiva o pausa sus tipos de entrada.' };
    }
    if (error.field === 'starts_at' || error.field === 'ends_at') {
        return { clave: 'fechas', texto: error.code === 'in_past' ? 'La fecha ya ha pasado: ponle una futura.' : 'Revisa las fechas.' };
    }
    if (error.field === 'payment_account') {
        return { clave: 'cuenta', texto: 'Conecta la cuenta de cobro para vender entradas de pago.' };
    }
    if (error.field === 'capacity' || error.field === 'box_office_quota') {
        return { clave: 'aforo', texto: error.message ?? 'El cupo de taquilla no puede ser mayor que el aforo.' };
    }
    return { clave: 'tipos', texto: error.message ?? 'Falta algo para publicar.' };
}

/**
 * Lista de comprobación antes de publicar. Lo que el panel sabe lo marca (tipos activos, fechas futuras, cupo ≤ aforo,
 * G513: sin «aforo ≥ suma de cupos»); la cuenta de cobro solo la sabe la API. Tras un `422`, mandan sus motivos.
 */
export function requisitos(e: Evento, tipos: readonly TipoEntrada[], fallos: readonly { field: string; code: string; message?: string }[] | null, ahora: Date = new Date()): Requisito[] {
    const motivos = (fallos ?? []).map(motivoPublicar);
    const falla = (clave: Requisito['clave']) => motivos.find((m) => m.clave === clave)?.texto ?? null;
    const activos = tipos.filter((t) => t.status === 'active').length;
    const soloListas = e.sale_mode === 'lists_only';
    const tiposLocal = soloListas ? activos === 0 : activos > 0;
    const fechasLocal = e.starts_at !== undefined && new Date(e.starts_at) > ahora;
    const aforoLocal = (e.box_office_quota ?? 0) <= e.capacity_detail.capacity;
    const fT = falla('tipos') ?? (tiposLocal ? null : soloListas ? 'Es solo de listas: archiva o pausa sus tipos de entrada.' : 'Añade al menos un tipo de entrada activo.');
    const fF = falla('fechas') ?? (fechasLocal ? null : 'La fecha ya ha pasado: ponle una futura.');
    const fA = falla('aforo') ?? (aforoLocal ? null : 'El cupo de taquilla no puede ser mayor que el aforo.');
    const fC = falla('cuenta');
    return [
        { clave: 'tipos', titulo: 'Tipos de entrada', ok: fT === null, texto: fT ?? (soloListas ? 'Solo listas: se entra con invitación.' : `${String(activos)} ${activos === 1 ? 'tipo activo' : 'tipos activos'}.`), ir: 'entradas' },
        { clave: 'fechas', titulo: 'Fechas', ok: fF === null, texto: fF ?? `${rangoEn(e.starts_at, e.ends_at, e.timezone)}.`, ir: 'datos' },
        { clave: 'aforo', titulo: 'Aforo', ok: fA === null, texto: fA ?? `${String(e.capacity_detail.capacity)} plazas para todo; taquilla ${String(e.box_office_quota ?? 0)}.`, ir: 'puerta' },
        { clave: 'cuenta', titulo: 'Cuenta de cobro', ok: fC === null, texto: fC ?? 'Solo hace falta si vendes entradas de pago.' },
    ];
}

// ---------------------------------------------------------------- Errores

/** Errores de campo de un `422`, por campo (el primero de cada uno). */
export function erroresDeCampo(error: unknown): Record<string, string> {
    if (!(error instanceof ErrorApi) || error.codigo !== 'validation_failed') {
        return {};
    }
    const textos: Record<string, string> = {};
    for (const e of error.errores) {
        textos[e.field] ??= e.message ?? (e.code === 'required' ? 'Falta este dato.' : e.code === 'too_short' ? 'Es demasiado corto.' : e.code === 'too_long' ? 'Es demasiado largo.' : (error.problema.detail ?? 'Revisa este dato.'));
    }
    if (Object.keys(textos).length === 1 && error.problema.detail !== undefined) {
        const [campo] = Object.keys(textos) as [string];
        const e = error.errores[0];
        if (e?.message === undefined && e?.code === 'invalid') {
            textos[campo] = error.problema.detail;
        }
    }
    return textos;
}

/** ¿Es el `409` de «cómo se entra» con entradas vendidas (G786)? Se mira solo el `code` (G856). */
export function esModoBloqueado(error: unknown): boolean {
    return error instanceof ErrorApi && error.codigo === 'sale_mode_locked';
}

/** Texto de un error al guardar (409, 412, 422 sin campo…) para el aviso de la ficha. */
export function textoErrorGuardar(error: unknown): string {
    if (!(error instanceof ErrorApi)) {
        return 'No se ha podido guardar. Prueba de nuevo.';
    }
    if (esModoBloqueado(error)) {
        return 'Cómo se entra ya no se cambia: hay entradas vendidas o pendientes de pago.';
    }
    if (error.codigo === 'nominative_locked') {
        // G860: código propio de la lista común; se mira solo `code` (G856).
        return 'Ya no se puede cambiar si las entradas son nominativas: hay entradas vendidas o pendientes de pago.';
    }
    if (error.codigo === 'version_conflict') {
        return 'Otra persona ha cambiado este evento. Lo hemos vuelto a leer: repite el cambio.';
    }
    if (error.codigo === 'commitments_pending') {
        const lista = (error.problema as { commitments?: { kind: string; count: number }[] }).commitments ?? [];
        const detalle = lista.map((c) => `${String(c.count)} ${c.kind}`).join(', ');
        return `${error.problema.detail ?? 'Hay compromisos que lo impiden.'}${detalle === '' ? '' : ` (${detalle})`}`;
    }
    return error.problema.detail ?? error.problema.title;
}

// ---------------------------------------------------------------- Ayuda («?»)

const t = (icono: NombreIcono, titulo: string, texto: string): TarjetaAyuda => ({ icono, titulo, texto });

export const AYUDA: Readonly<Record<'lista' | Pestana | 'nuevo' | 'tipo', readonly TarjetaAyuda[]>> = {
    lista: [
        t('ticket', 'Eventos', 'Se crean en borrador y se venden al publicarlos. Cada uno tiene un aforo único para entradas, listas y taquilla.'),
        t('gauge', 'La barra de aforo', 'Negro: vendidas. Amarillo: pendientes de pago (30 minutos). Gris: invitados de las listas. Claro: cupo de taquilla sin vender.'),
        t('lock', 'Con entradas vendidas', 'Ya no se despublica: si no se celebra, se cancela y se devuelve el dinero a todos.'),
    ],
    datos: [
        t('file-text', 'Datos', 'Nombre, descripción, sede y fechas. Lo ven tus clientes en la web y en la ficha del evento.'),
        t('calendar-clock', 'La fecha', 'En borrador la cambias aquí. Publicado, en «Fecha»: eliges si es una corrección o un cambio real con aviso.'),
    ],
    entradas: [
        t('ticket', 'Tipos de entrada', 'Cada tipo tiene su precio, su cupo y sus fechas de venta. El precio de lo ya vendido no cambia.'),
        t('percent', 'Gastos de gestión', '1 € + 1,5 % por entrada, que paga el comprador aparte del precio.'),
        t('user-check', 'Nominativas', 'Se pide el nombre de cada asistente; todas las entradas llegan al comprador.'),
    ],
    puerta: [
        t('door-open', 'Aforo único', 'Entradas, invitados de las listas y cupo de taquilla salen del mismo aforo: nunca se vende de más.'),
        t('wifi-off', 'Cupo de taquilla', 'Lo que la taquilla puede vender aunque se caiga la conexión. Se reserva al preparar la puerta.'),
    ],
    publicar: [
        t('megaphone', 'Publicar', 'Aparece en tu web, el widget y el asistente, y se activan los enlaces de los RRPP.'),
        t('lock', 'Despublicar', 'Solo sin entradas vendidas ni pedidos pendientes de pago. Si no, se cancela el evento.'),
    ],
    fecha: [
        t('pencil', 'Corrección', 'Te equivocaste al ponerla: se corrige sin avisar a nadie. Queda en Actividad.'),
        t('bell-ring', 'Cambio real', 'Lluvia, una baja… Avisamos a quien tiene entrada, invitación o reservado, y pueden cancelar gratis hasta la fecha que elijas.'),
        t('qr-code', 'Los pases', 'Siguen valiendo: el QR no cambia.'),
    ],
    cancelar: [
        t('rotate-ccw', 'Reembolso íntegro', 'Se devuelve todo, también los gastos de gestión.'),
        t('banknote', 'Taquilla', 'Lo pagado en efectivo o datáfono lo devuelves tú en el local.'),
        t('receipt', 'Informe', 'Verás cuántos pedidos se han devuelto y si alguno ha fallado.'),
    ],
    informe: [
        t('receipt', 'Informe de cancelación', 'Pedidos cancelados, reembolsados y fallidos, e importes. Los reembolsos siguen en segundo plano.'),
        t('refresh-cw', 'Si un reembolso falla', 'Reinténtalo desde el pedido, en Entradas › Pedidos.'),
    ],
    tipo: [
        t('ticket', 'Tipo de entrada', 'Precio, cupo, fechas de venta y dónde se vende. Puedes pausarlo sin perder lo vendido.'),
        t('percent', 'Gastos de gestión', 'Los paga el comprador: 1 € + 1,5 % por entrada. Las gratis no tienen.'),
    ],
    nuevo: [
        t('file-text', 'Borrador', 'Nadie lo ve hasta que lo publiques.'),
        t('door-open', 'Aforo', 'Uno solo para entradas, listas y taquilla. El cupo de taquilla no puede ser mayor que el aforo.'),
    ],
};

/** Enlace a un pedido en Entradas › Pedidos (10.6), para los reembolsos fallidos (G386). */
export function rutaPedido(id: string): string {
    return `/pedidos?apartado=pedidos&pedido=${encodeURIComponent(id)}`;
}

/** Motivo de cambiar la fecha o cancelar (`Reason`: de 10 a 500 letras). */
export function errorMotivo(motivo: string): string | null {
    const n = motivo.trim().length;
    return n < 10 ? 'Escribe el motivo (al menos 10 letras).' : n > 500 ? 'Como mucho 500 letras.' : null;
}

// ---------------------------------------------------------------- Operaciones

export type OpsEventos = Operaciones & OperacionesEventos;

/**
 * Las operaciones de Eventos están en `api/ops/eventos.ts` y se juntan en `crearOperaciones`; hasta que se den de alta allí,
 * el tipo se completa aquí (sin efecto una vez dadas de alta).
 */
export function opsDe(ops: Operaciones): OpsEventos {
    return ops;
}

/** Informe de un cambio de fecha con las cifras de `DateChange` (G786). «Mantienen» = afectados − cancelan gratis. */
export function informeCambio(c: CambioDeFecha): { titulo: string; cifra: string; unidad?: string; nota: string }[] {
    if (c.kind === 'correction') {
        return [];
    }
    const afectados = c.affected_orders ?? 0;
    const gratis = c.free_cancellations ?? 0;
    return [
        { titulo: 'Avisos', cifra: String(c.notified_orders ?? 0), nota: 'pedidos avisados' },
        { titulo: 'Cancelan gratis', cifra: String(gratis), unidad: 'pedidos', nota: `de ${String(afectados)} pedidos` },
        { titulo: 'Reembolsado', cifra: c.refunded_amount === undefined ? '0,00' : cifraDinero(c.refunded_amount), unidad: '€', nota: (c.refunds_failed ?? 0) > 0 ? `${String(c.refunds_failed)} fallidos: míralos en Pedidos` : 'Con Stripe, íntegro' },
        { titulo: 'Mantienen', cifra: String(Math.max(0, afectados - gratis)), unidad: 'pedidos', nota: 'siguen con su entrada' },
    ];
}

/** `assigned_to_devices` del `409 door_quota_below_assigned` de «Preparar ahora» (G948); null si es otro error. */
export function cupoYaRepartido(err: unknown): number | null {
    if (!(err instanceof ErrorApi) || err.codigo !== 'door_quota_below_assigned') return null;
    const cifra = (err.problema as { assigned_to_devices?: unknown }).assigned_to_devices;
    return typeof cifra === 'number' ? cifra : null;
}
